import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { MerchantAuthProvider } from './contexts/MerchantAuthContext';
import FlutterServicesAPI from './services/flutterServices';

// Website Components
import { LandingPage, PricingPage, PricingNew, FAQsPage, TermsPage, PrivacyPage, ContactUsPage, TestInstaPage, AppDownloadPage, BlogsList, CaseStudyList } from './components/Website';
import Blog1 from './components/Website/Blogs/BlogDetails/Blog1';
import Blog2 from './components/Website/Blogs/BlogDetails/Blog2';
import Blog3 from './components/Website/Blogs/BlogDetails/Blog3';
import CaseStudy1 from './components/Website/CaseStudy/CaseStudyDetails/CaseStudy1';

// Admin Components
import { MerchantPermissionProvider } from './contexts/MerchantPermissionContext';
import Login from './components/Auth/Login';
import AdminForgotPassword from './components/Auth/ForgotPassword';
import AdminResetPassword from './components/Auth/ResetPassword';
import Dashboard from './components/Dashboard/Dashboard';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Layout from './components/Layout/Layout';
import Users from './components/Users/Users';
import Roles from './components/Roles/Roles';
import Departments from './components/Departments/Departments';
import Companies from './components/Companies/Companies';
import Modules from './components/Modules/Modules';
import Permissions from './components/Permissions/Permissions';
import UserRoles from './components/UserRoles/UserRoles';
import AuditLogs from './components/Audit/AuditLogs';
import Profile from './components/Profile/Profile';
import DemoPage from './components/Merchant/Profile/demo';
import TestModule from './components/TestModule/TestModule';
import DynamicModule from './components/DynamicModule/DynamicModule';
import NotFound from './components/Error/NotFound';
import AccessDenied from './components/Error/AccessDenied';
import PermissionGuard from './components/Auth/PermissionGuard';
import AdminRedirect from './components/AdminRedirect/AdminRedirect';
import FeedbackManagement from './components/FeedbackManagement/FeedbackManagement';
import ContactEnquiries from './components/ContactEnquiries/ContactEnquiries.js';
import AddonServices from './components/AddonServices/AIModelFaceManagement';
import AdminSubscriptionManagement from './components/AdminSubscriptionManagement/AdminSubscriptionManagement';
import MerchantSubscription from './components/Merchant/Subscription/MerchantSubscription';

// Subscription v2 — admin
import PlansManagement        from './components/subscription_v2/admin/Plans/PlansManagement';
import SubscriptionsManagement from './components/subscription_v2/admin/Subscriptions/SubscriptionsManagement';
import AuditLogsPage          from './components/subscription_v2/admin/AuditLogs/AuditLogsPage';
import InvoicesManagement     from './components/subscription_v2/admin/Invoices/InvoicesManagement';
import CouponsManagement      from './components/subscription_v2/admin/Coupons/CouponsManagement';
import ContentPricingManagement from './components/subscription_v2/admin/ContentPricing/ContentPricingManagement';
// Subscription v2 — merchant
import OutOfTokens          from './components/subscription_v2/merchant/OutOfTokens/OutOfTokens';
import MySubscriptionPage   from './components/subscription_v2/merchant/MySubscription/MySubscriptionPage';
import RazorpayRedirectPage  from './components/Merchant/Payment/RazorpayRedirectPage';
import RazorpayCallbackPage  from './components/Merchant/Payment/RazorpayCallbackPage';
import DualAccessPanel from './components/Common/DualAccessPanel';

import RoutePermissionGuard from './components/Common/RoutePermissionGuard';
// Merchant components
import MerchantLogin from './components/Merchant/Auth/MerchantLogin';
import MerchantRegister from './components/Merchant/Auth/MerchantRegister';
import WhatsAppSignUp from './components/Merchant/Auth/WhatsAppSignUp';
import WhatsAppHelpRedirect from './components/WhatsAppHelpRedirect/WhatsAppHelpRedirect';
import SMSSignUp from './components/Merchant/Auth/SMSSignUp';
import EmailVerification from './components/Merchant/Auth/EmailVerification';
import MerchantOTP from './components/Merchant/Auth/MerchantOTP';
import ForgotPassword from './components/Merchant/Auth/ForgotPassword';
import ResetPassword from './components/Merchant/Auth/ResetPassword';
import SetupPassword from './components/Auth/SetupPassword';
import MerchantProtectedRoute from './components/Merchant/Auth/MerchantProtectedRoute';
import MerchantLayout from './components/Merchant/Layout/MerchantLayout';
import MerchantDashboard from './components/Merchant/Dashboard/MerchantDashboard';
import AIGenerationWizard from './components/Merchant/AIGeneration/AIGenerationWizard';
import GenerationHistory from './components/Merchant/History/GenerationHistory';
import Gallery from './components/Merchant/Gallery/Gallery';
import GuestGallery from './components/Merchant/Guest/GuestGallery';
import CreditsManagement from './components/Merchant/Credits/CreditsManagement';
import MerchantProfile from './components/Merchant/Profile/MerchantProfile';
import ProfilePage from './components/Merchant/Profile/ProfilePage';
import BrandsPage from './components/Merchant/Profile/BrandsPage';
import NotificationsPage from './components/Merchant/Profile/NotificationsPage';
import SecurityPage from './components/Merchant/Profile/SecurityPage';
import AIModelStudioPage from './components/Merchant/Profile/AIModelStudioPage';
import CustomerPhotoPage  from './components/Merchant/CustomerPhoto/CustomerPhotoPage';
import MerchantManagement from './components/MerchantManagement/MerchantManagement';
import CatalogPage from './components/Merchant/Catalog/CatalogPage';
import CatalogViewer from './components/Merchant/Catalog/CatalogViewer';
import BulkUploadPage     from './components/Merchant/BulkUpload/BulkUploadPage';
import PDFCataloguePage   from './components/Merchant/PDFCatalogue/PDFCataloguePage';
import ReelsPage          from './components/Merchant/Reels/ReelsPage';
import PremiumShotsPage   from './components/Merchant/PremiumShots/PremiumShotsPage';
import UGCVideosPage      from './components/Merchant/UGCVideos/UGCVideosPage';
import APIAccessPage      from './components/Merchant/APIAccess/APIAccessPage';
import CRMIntegrationPage from './components/Merchant/CRMIntegration/CRMIntegrationPage';
import CreditSystemManagement from './components/CreditSystemManagement/CreditSystemManagement';
import MasterDataConfiguration from './components/MasterDataConfiguration/MasterDataConfiguration';
import MiddlewareData from './components/MiddlewareData/MiddlewareData';
import './App.css';
import { UnauthedChatCleanup, ChatWidgetLoader } from './components/Common';


// App.js - Main routing configuration
// - Merchant interface routes at root path (/)
// - Admin panel routes at /glam-admin/*
// - Protected routes redirect to correct login paths
// - Fixed admin redirects to maintain /glam-admin path structure
// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Helvetica Neue", Arial, "sans-serif"',
    letterSpacing: '0.4px',
  },
  components: {
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            color: '#666',
            '&.Mui-selected': {
              backgroundColor: '#6318C6',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5a14b3',
              },
            },
            '&:hover': {
              backgroundColor: 'rgba(99, 24, 198, 0.1)',
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#EFEFFF !important',
            '&:hover': {
              backgroundColor: '#EFEFFF !important'
            }
          },
          '&:hover': {
            backgroundColor: '#EFEFFF !important'
          }
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          '& .MuiTablePagination-toolbar': {
            alignItems: 'center',
            minHeight: '52px',
            paddingLeft: '16px',
            paddingRight: '16px'
          },
          '& .MuiTablePagination-selectLabel': {
            marginBottom: 0,
            alignSelf: 'center'
          },
          '& .MuiTablePagination-select': {
            marginBottom: 0,
            alignSelf: 'center'
          },
          '& .MuiTablePagination-displayedRows': {
            marginBottom: 0,
            alignSelf: 'center'
          },
          '& .MuiTablePagination-actions': {
            marginBottom: 0,
            alignSelf: 'center'
          }
        }
      }
    }
  },
});

function App() {


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <MerchantAuthProvider>
          <MerchantPermissionProvider>
            <Routes>
              {/* Dual Access Panel */}
              <Route path="/dual-access" element={<DualAccessPanel />} />

              {/* Razorpay redirect — public, no auth, opened in external browser by Flutter */}
              <Route path="/razorpay-redirect" element={<RazorpayRedirectPage />} />
              {/* Razorpay callback — handles Flutter WebView navigation after payment */}
              <Route path="/razorpay/callback" element={<RazorpayCallbackPage />} />

              {/* Merchant Interface Routes (Root Path) */}
              <Route path="/*" element={<MerchantApp />} />

              {/* Admin Panel Routes */}
              <Route path="/glam-admin/*" element={<AdminApp />} />

              {/* 404 catch-all route - must be last */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </MerchantPermissionProvider>
        </MerchantAuthProvider>
      </Router>
    </ThemeProvider>
  );
}

// Merchant Application
function MerchantApp() {
  return (
    <>
      <UnauthedChatCleanup />
      <ChatWidgetLoader />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/demo" element={<DemoPage />} />
        {/* Redirect pricing to dashboard in Flutter app, show pricing page for web */}
        <Route
          path="/pricing"
          element={
            FlutterServicesAPI.isFlutterApp()
              ? <Navigate to="/dashboard" replace />
              : <PricingPage />
          }
        />
        <Route path="/pricing-new" element={<PricingNew />} />
        <Route path="/faqs" element={<FAQsPage />} />
        <Route path="/terms-conditions" element={<TermsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPage />} />
        <Route path="/contact-us" element={<ContactUsPage />} />
        <Route path="/appdownload" element={<AppDownloadPage />} />
        <Route path="/blogs" element={<BlogsList />} />
        <Route path="/case-studies" element={<CaseStudyList />} />
        <Route path="/case-studies/:slug" element={<CaseStudy1 />} />
        <Route path="/How-Jewellery-Brands-Can-Scale-Product-Catalogs-Without-Increasing-Photoshoot-Costs" element={<Blog1 />} />
        <Route path="/AI-Jewellery-Images-12-Mistakes-That-Make-Products-Look-Fake-And-Fixes" element={<Blog2 />} />
        <Route path="/Bust-vs-Full-Human-Model-Which-Jewellery-Visual-Converts-Better" element={<Blog3 />} />
        <Route path="/whatsapphelp" element={<WhatsAppHelpRedirect />} />
        <Route path="/login" element={<MerchantLogin />} />
        <Route path="/register" element={<MerchantRegister />} />
        <Route path="/whatsapp-signup" element={<WhatsAppSignUp />} />
        <Route path="/sms-signup" element={<SMSSignUp />} />
        <Route path="/verify-otp" element={<MerchantOTP />} />
        <Route path="/guest-gallery" element={<GuestGallery />} />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/setup-password/:token" element={<SetupPassword />} />

        {/* Public catalog viewer — no auth required */}
        <Route path="/catalog/:token" element={<CatalogViewer />} />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <MerchantProtectedRoute>
              <MerchantLayout>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route
                    path="/dashboard"
                    element={
                      <RoutePermissionGuard moduleName="Merchant Dashboard" actionName="view" showAccessDenied={true}>
                        <MerchantDashboard />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/generate"
                    element={
                      <RoutePermissionGuard moduleName="AI Generation" actionName="view" showAccessDenied={true}>
                        <AIGenerationWizard />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/gallery"
                    element={
                      <RoutePermissionGuard moduleName="Gallery" actionName="view" showAccessDenied={true}>
                        <Gallery />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/catalogs"
                    element={
                      <RoutePermissionGuard moduleName="Catalog Management" actionName="view" showAccessDenied={true}>
                        <CatalogPage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/bulk-upload"
                    element={
                      <RoutePermissionGuard moduleName="Bulk Upload" actionName="view" showAccessDenied={true}>
                        <BulkUploadPage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/pdf-catalogue"
                    element={
                      <RoutePermissionGuard moduleName="PDF Catalogue" actionName="view" showAccessDenied={true}>
                        <PDFCataloguePage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/reels"
                    element={
                      <RoutePermissionGuard moduleName="Reels" actionName="view" showAccessDenied={true}>
                        <ReelsPage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/premium-shots"
                    element={
                      <RoutePermissionGuard moduleName="Premium Shots" actionName="view" showAccessDenied={true}>
                        <PremiumShotsPage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/ugc-videos"
                    element={
                      <RoutePermissionGuard moduleName="UGC Videos" actionName="view" showAccessDenied={true}>
                        <UGCVideosPage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/api-access"
                    element={
                      <RoutePermissionGuard moduleName="API Access" actionName="view" showAccessDenied={true}>
                        <APIAccessPage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/crm-integration"
                    element={
                      <RoutePermissionGuard moduleName="CRM Integration" actionName="view" showAccessDenied={true}>
                        <CRMIntegrationPage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/history"
                    element={
                      <RoutePermissionGuard moduleName="Generation History" actionName="view" showAccessDenied={true}>
                        <GenerationHistory />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/credits"
                    element={
                      <RoutePermissionGuard moduleName="Credits Management" actionName="view" showAccessDenied={true}>
                        <CreditsManagement />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <RoutePermissionGuard moduleName="Merchant Profile" actionName="view" showAccessDenied={true}>
                        <ProfilePage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/profile/brands"
                    element={
                      <RoutePermissionGuard moduleName="Merchant Profile" actionName="view" showAccessDenied={true}>
                        <BrandsPage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/profile/notifications"
                    element={
                      <RoutePermissionGuard moduleName="Merchant Profile" actionName="view" showAccessDenied={true}>
                        <NotificationsPage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/profile/security"
                    element={
                      <RoutePermissionGuard moduleName="Merchant Profile" actionName="view" showAccessDenied={true}>
                        <SecurityPage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/profile/ai-model-studio"
                    element={
                      <RoutePermissionGuard moduleName="AI Model Studio" actionName="view" showAccessDenied={true}>
                        <AIModelStudioPage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/subscription"
                    element={
                      <RoutePermissionGuard moduleName="Merchant Subscription" actionName="view" showAccessDenied={true}>
                        <MerchantSubscription />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/ai-model-studio"
                    element={
                      <RoutePermissionGuard moduleName="AI Model Studio" actionName="view" showAccessDenied={true}>
                        <AIModelStudioPage />
                      </RoutePermissionGuard>
                    }
                  />
                  <Route
                    path="/customer-photo"
                    element={
                      <RoutePermissionGuard moduleName="Customer Photo" actionName="view" showAccessDenied={true}>
                        <CustomerPhotoPage />
                      </RoutePermissionGuard>
                    }
                  />
                  {/* Subscription v2 merchant routes */}
                  <Route path="/my-subscription"     element={<MySubscriptionPage />} />
                  <Route path="/v2/my-subscription"  element={<Navigate to="/my-subscription" replace />} />
                  <Route path="/v2/plans"            element={<Navigate to="/credits?tab=0" replace />} />
                  <Route path="/v2/marketplace"      element={<Navigate to="/credits" replace />} />
                  <Route path="/v2/topup"            element={<Navigate to="/credits?tab=0" replace />} />
                  <Route path="/v2/out-of-tokens"    element={<OutOfTokens />} />

                  <Route path="/access-denied" element={<AccessDenied />} />
                  <Route path="/merchantmanagement" element={<MerchantManagement />} />
                  <Route path="/creditsystemmanagement" element={<CreditSystemManagement />} />
                  <Route path="/masterdataconfiguration" element={<MasterDataConfiguration />} />
                  <Route path="/middlewaredata" element={<MiddlewareData />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </MerchantLayout>
            </MerchantProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

// Admin Panel Application
function AdminApp() {
  return (
    <AuthProvider>
      <PermissionProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<AdminForgotPassword />} />
          <Route path="/reset-password" element={<AdminResetPassword />} />

          {/* Protected admin routes with proper base path for redirects */}
          <Route
            path="/*"
            element={
              <ProtectedRoute basePath="/glam-admin">
                <Layout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/glam-admin/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route
                      path="/users"
                      element={
                        <PermissionGuard module="Users" action="view" showInLayout={true}>
                          <Users />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/roles"
                      element={
                        <PermissionGuard module="Roles" action="view" showInLayout={true}>
                          <Roles />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/departments"
                      element={
                        <PermissionGuard module="Departments" action="view" showInLayout={true}>
                          <Departments />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/companies"
                      element={
                        <PermissionGuard module="Companies" action="view" showInLayout={true}>
                          <Companies />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/modules"
                      element={
                        <PermissionGuard module="Modules" action="view" showInLayout={true}>
                          <Modules />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/permissions"
                      element={
                        <PermissionGuard module="Permissions" action="view" showInLayout={true}>
                          <Permissions />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/userroles"
                      element={
                        <PermissionGuard module="UserRoles" action="view" showInLayout={true}>
                          <UserRoles />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/audit-logs"
                      element={
                        <PermissionGuard module="Settings" action="view" showInLayout={true}>
                          <AuditLogs />
                        </PermissionGuard>
                      }
                    />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/testmodule" element={<TestModule />} />
                    <Route
                      path="/merchantmanagement"
                      element={
                        <PermissionGuard module="MerchantManagement" action="view" showInLayout={true}>
                          <MerchantManagement />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/creditsystemmanagement"
                      element={
                        <PermissionGuard module="CreditSystemManagement" action="view" showInLayout={true}>
                          <CreditSystemManagement />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/masterdataconfiguration"
                      element={
                        <PermissionGuard module="MasterDataConfiguration" action="view" showInLayout={true}>
                          <MasterDataConfiguration />
                        </PermissionGuard>
                      }
                    />
                    {/* Subscription v2 admin routes */}
                    <Route
                      path="/plans_addons"
                      element={
                        <PermissionGuard module="Plans & Add-ons" action="view" showInLayout={true}>
                          <PlansManagement />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/subscriptions_v2"
                      element={
                        <PermissionGuard module="Subscriptions" action="view" showInLayout={true}>
                          <SubscriptionsManagement />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/subscriptions_v2/audit-logs"
                      element={
                        <PermissionGuard module="Subscriptions" action="view" showInLayout={true}>
                          <AuditLogsPage />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/invoices"
                      element={
                        <PermissionGuard module="Invoices" action="view" showInLayout={true}>
                          <InvoicesManagement />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/coupons"
                      element={
                        <PermissionGuard module="Coupons" action="view" showInLayout={true}>
                          <CouponsManagement />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/content_pricing"
                      element={
                        <PermissionGuard module="Content Pricing" action="view" showInLayout={true}>
                          <ContentPricingManagement />
                        </PermissionGuard>
                      }
                    />

                    <Route
                      path="/middlewaredata"
                      element={
                        <PermissionGuard module="MiddlewareData" action="view" showInLayout={true}>
                          <MiddlewareData />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/feedback"
                      element={
                        <PermissionGuard module="FeedbackManagement" action="view" showInLayout={true}>
                          <FeedbackManagement />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/contactenquiries"
                      element={
                        <PermissionGuard module="ContactEnquiries" action="view" showInLayout={true}>
                          <ContactEnquiries />
                        </PermissionGuard>
                      }
                    />
                    <Route
                      path="/contactenquiries"
                      element={
                        <PermissionGuard module="ContactEnquiries" action="view" showInLayout={true}>
                          <ContactEnquiries />
                        </PermissionGuard>
                      }
                    />

                    <Route
                      path="/aimodelfacemanagement"
                      element={
                        <PermissionGuard module="AiModelFaceManagement" action="view" showInLayout={true}>
                          <AddonServices />
                        </PermissionGuard>
                      }
                    />

                    {/* Dynamic route for all other modules */}
                    <Route path=":moduleName" element={<DynamicModule />} />
                    {/* 404 catch-all route - must be last */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </PermissionProvider>
    </AuthProvider>
  );
}

export default App;